import Component from "../../Component";
import BaseApi from "./BaseApi";
import { Employee } from "../types";
import BookingApi from "./BookingApi";

export default class EmployeeApi extends BaseApi {
    constructor(component: Component) {
        super(component);
    }

    public async fetchAllEmployees(): Promise<Employee[]> {
        return this.fetchWithAuth<Employee[]>("/employee");
    }

    public async fetchFavoriteSeats(): Promise<any> {
        return this.fetchWithAuth<any>("/employee/favoriteseats");
    }

    public async addFavoriteSeat(seatId: string): Promise<void> {
        const body = { seatId };
        try {
            await this.postWithAuth<void>("/employee/favoriteseats", body);
        } catch (error) {
            console.error("EmployeeApi: Error adding favorite seat:", seatId, error);
            throw error;
        }
    }

    public async removeFavoriteSeat(seatId: string): Promise<void> {
        console.log("EmployeeApi: Removing favorite seat:", seatId);
        try {
            await this.deleteWithAuth<void>(`/employee/favoriteseats/${seatId}`);
        } catch (error) {
            console.error("EmployeeApi: Error removing favorite seat:", seatId, error);
            throw error;
        }
    }

    public async fetchOwnHomeZones(): Promise<any> {
        return this.fetchWithAuth<any>("/employee/homezones");
    }

    /**
     * Fetches employees with suggestions (previous bookings) shown first
     * Returns the employee data for the controller to handle model setting
     * @returns Promise<{employees: any[], suggestedEmployees: any[]}>
     */
    public async fetchEmployeesWithSuggestions(): Promise<{
        employees: any[];
        suggestedEmployees: any[];
    }> {
        try {
            console.log("EmployeeApi: Starting fetchEmployeesWithSuggestions");
            // Get booking API through the component
            const bookingApi = new BookingApi(this.component);

            // Get suggested employees from booking API
            console.log("EmployeeApi: Calling fetchSuggestedEmployees");
            const suggestedEmployees = await bookingApi.fetchSuggestedEmployees();
            console.log("EmployeeApi: Suggested employees received:", suggestedEmployees);

            // Get all employees as fallback
            console.log("EmployeeApi: Fetching all employees");
            const allEmployees = await this.fetchAllEmployees();
            console.log("EmployeeApi: All employees count:", allEmployees.length);

            // Combine suggestions with all employees, avoiding duplicates
            const employeeMap = new Map();

            // Add suggested employees first (they'll appear at the top)
            suggestedEmployees.forEach((emp: any) => {
                employeeMap.set(emp.id, { ...emp, isSuggested: true });
            });

            // Add remaining employees
            allEmployees.forEach((emp: any) => {
                if (!employeeMap.has(emp.id)) {
                    employeeMap.set(emp.id, { ...emp, isSuggested: false });
                }
            });

            const combinedEmployees = Array.from(employeeMap.values());

            console.log("Fetched employees with suggestions:", {
                totalEmployees: combinedEmployees.length,
                suggestedCount: suggestedEmployees.length,
                sampleSuggestedEmployee: suggestedEmployees[0],
                sampleCombinedEmployee: combinedEmployees[0]
            });

            return {
                employees: combinedEmployees,
                suggestedEmployees: suggestedEmployees
            };
        } catch (error) {
            console.error("Error fetching employees with suggestions:", error);
            // Fallback to regular employee loading
            try {
                const employees = await this.fetchAllEmployees();
                return {
                    employees: employees,
                    suggestedEmployees: []
                };
            } catch (fallbackError) {
                console.error("Failed to fetch employees", fallbackError);
                return {
                    employees: [],
                    suggestedEmployees: []
                };
            }
        }
    }
}
